import { type Locale, routing, usePathname, useRouter } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import * as SelectPrimitive from '@radix-ui/react-select';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useParams, useSearchParams } from 'next/navigation';
import { forwardRef, useTransition } from 'react';
import Flag from 'react-flagkit';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Map locale codes to country codes for flags
const localeToCountry: Record<string, string> = {
  en: 'US',
  sk: 'SK',
};

// Custom SelectItem without checkmark indicator
const CustomSelectItem = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none  py-1 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
CustomSelectItem.displayName = SelectPrimitive.Item.displayName;

export default function LocaleSwitcher() {
  const locale = useLocale();
  const t = useTranslations('LocaleSwitcher');
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();

  function onSelectChange(nextLocale: Locale) {
    startTransition(() => {
      // Preserve the current search parameters
      const query = Object.fromEntries(searchParams.entries());

      router.replace(
        // @ts-expect-error -- TypeScript will validate that only known `params`
        // are used in combination with a given `pathname`. Since the two will
        // always match for the current route, we can skip runtime checks.
        { pathname, params, query },
        { locale: nextLocale }
      );
    });
  }

  return (
    <div
      className={clsx(
        'flex items-center justify-center h-9',
        isPending && 'transition-opacity [&:disabled]:opacity-30'
      )}
    >
      <Select
        defaultValue={locale}
        onValueChange={onSelectChange}
        disabled={isPending}
      >
        <SelectTrigger
          // The trigger renders only a flag SVG, so without this the
          // combobox button has no accessible name (#8708, axe button-name).
          aria-label={t('label')}
          className='w-[44px] h-9 bg-transparent border-none focus:ring-0 focus:ring-offset-0 p-2 flex items-center justify-center'
        >
          <SelectValue>
            <Flag country={localeToCountry[locale] ?? 'SK'} size={20} />
          </SelectValue>
        </SelectTrigger>
        <SelectContent className='w-[70px] min-w-[30px] p-0'>
          <SelectGroup className='p-0'>
            {routing.locales.map((cur) => (
              <CustomSelectItem key={cur} value={cur} className='p-1'>
                <div className='flex'>
                  <Flag country={localeToCountry[cur] ?? 'SK'} size={25} />
                </div>
              </CustomSelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}
